import { QueryBuilderFilterType, RuleValueInput } from '@ardoq/query-builder';
import { FlexBox } from '@ardoq/layout';
import { CommonDefaultFilterKeys } from '@ardoq/api-types';
import { useFormattingContext } from '../useFormattingContext';
import { type I18nLocale, t } from '@ardoq/i18n';
import { InvertCheckbox } from './atoms';
import { SelectComparator } from './SelectComparator';
import { showInvertCheckbox } from '../utils';
import { FormattingColorPicker } from '../../FormattingColorPicker';
import { FormattingRuleBodyProperties } from './types';
import { FormattingRuleColorPickerOnly } from './FormattingRuleColorPickerOnly';
import { buildColorWarningKey } from '../colorWarnings';
import { ValueWarningMessage } from './ValueWarningMessage';
import { FormattingRuleOperator } from '@ardoq/api-types';
import { InputsWrapper } from '../../../atoms';
import { ColorPickerWrapper } from './atoms';
import { isRuleVersionSupported } from '../../../utils';
import { buildNumberWarningKey } from '../numberWarnings';
import { getReversedRangeWarning } from '../reversedRangeWarnings';
import { RuleValueWrapper } from './RuleValueWrapper';

const getValuePlaceholder = (
  filterType: QueryBuilderFilterType,
  i18nLocale: I18nLocale
) => {
  switch (filterType) {
    case QueryBuilderFilterType.ASYNC_COMPONENT_OID:
    case QueryBuilderFilterType.ASYNC_REFERENCE_OID:
    case QueryBuilderFilterType.ASYNC_PARENT:
    case QueryBuilderFilterType.ASYNC_SOURCE:
    case QueryBuilderFilterType.ASYNC_TARGET:
    case QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE:
    case QueryBuilderFilterType.SELECT_MULTIPLE:
      return t(
        i18nLocale,
        'perspectives:formattingRuleRow.searchAndSelect',
        'Search and select'
      );
    case QueryBuilderFilterType.NUMBER:
      return t(
        i18nLocale,
        'perspectives:formattingRuleRow.typeNumberValue',
        'Type a number value'
      );
  }
  return undefined;
};

type FormattingRuleRowByComponentOrReferenceBodyProperties =
  FormattingRuleBodyProperties & {
    /** Workspace mode (false) renders the legacy rule row, viewpoint mode (true) the new one. */
    isNewConditionalFormatting: boolean;
  };

export const FormattingRuleRowByComponentOrReferenceBody = ({
  rule,
  filter,
  comparators,
  isNewConditionalFormatting,
}: FormattingRuleRowByComponentOrReferenceBodyProperties) => {
  const {
    updateRule,
    asyncSuggestionsLoaders,
    asyncLabelLoaders,
    ruleInfo,
    locale,
    i18nLocale,
  } = useFormattingContext();
  const { __id: ruleId, comparator, color, field, value, isInverted } = rule;
  const { id: filterId, filterType } = filter;
  const { colorWarnings = {}, numberWarnings = {} } = ruleInfo[rule.__id] || {};
  const colorWarningMessage = colorWarnings[buildColorWarningKey(rule.color)];

  // Build number warning key only for number rules
  const numberWarningMessage =
    field && filterType === QueryBuilderFilterType.NUMBER
      ? numberWarnings[buildNumberWarningKey(field, comparator, value)]
      : undefined;

  const isColorPickerOnly =
    isNewConditionalFormatting &&
    (filterId === CommonDefaultFilterKeys.TYPE ||
      filterType === QueryBuilderFilterType.BOOLEAN ||
      comparator === FormattingRuleOperator.IS_EMPTY);
  const valuePlaceholder = isNewConditionalFormatting
    ? getValuePlaceholder(filterType, i18nLocale)
    : undefined;

  const isRuleUnsupported = !isRuleVersionSupported(
    isNewConditionalFormatting,
    rule.version
  );

  const reversedRangeWarning = getReversedRangeWarning({
    i18nLocale,
    filterType,
    comparator,
    value,
  });
  const valueWarningMessage = reversedRangeWarning ?? numberWarningMessage;

  const hasValueInputWarning =
    Boolean(colorWarningMessage) || Boolean(valueWarningMessage);

  if (isColorPickerOnly) {
    return (
      <>
        <InputsWrapper $isDisabled={isRuleUnsupported}>
          <SelectComparator
            selectedComparator={comparator}
            comparators={comparators || filter.comparators}
            onValueChange={comparator => {
              if (!comparator) return; // shouldn't happen
              updateRule(ruleId, { comparator });
            }}
            isNewConditionalFormatting={isNewConditionalFormatting}
            filterType={filterType}
          />
        </InputsWrapper>
        <InputsWrapper $isDisabled={isRuleUnsupported}>
          <FormattingRuleColorPickerOnly rule={rule} />
        </InputsWrapper>
      </>
    );
  }

  return (
    <>
      <InputsWrapper $isDisabled={isRuleUnsupported}>
        <SelectComparator
          selectedComparator={comparator}
          comparators={comparators || filter.comparators}
          onValueChange={comparator => {
            if (!comparator) return; // shouldn't happen
            updateRule(ruleId, { comparator });
          }}
          isNewConditionalFormatting={isNewConditionalFormatting}
          filterType={filterType}
        />
      </InputsWrapper>
      <InputsWrapper $isDisabled={isRuleUnsupported} $flex={2}>
        <FlexBox
          flex={2}
          flexDirection="column"
          gap="xsmall"
          style={{ minWidth: 0 }}
        >
          <FlexBox align="center" gap="small" style={{ minWidth: 0 }}>
            {isNewConditionalFormatting ? (
              <ColorPickerWrapper $hasWarning={Boolean(colorWarningMessage)}>
                <FormattingColorPicker
                  dataTestId="formatting-rule-color-picker"
                  value={color}
                  onValueChange={color => updateRule(ruleId, { color })}
                  isNewConditionalFormatting={isNewConditionalFormatting}
                />
              </ColorPickerWrapper>
            ) : null}
            {filterType && comparator && (
              <RuleValueWrapper
                isNewConditionalFormatting={isNewConditionalFormatting}
                warningMessage={valueWarningMessage}
              >
                <RuleValueInput
                  key={field}
                  operator={comparator}
                  asyncSuggestionsLoaders={asyncSuggestionsLoaders}
                  asyncLabelLoaders={asyncLabelLoaders}
                  filterType={filterType}
                  filter={filter}
                  placeholder={valuePlaceholder}
                  // Formatting rules fallback to null, but null is not a valid value for this input type, undefined is.
                  value={value ?? undefined}
                  onValueChange={value =>
                    updateRule(ruleId, {
                      value: Number.isNaN(value) ? null : value,
                    })
                  }
                  hasWarning={hasValueInputWarning}
                  hideWarningIcon={!hasValueInputWarning}
                  isNewConditionalFormatting={isNewConditionalFormatting}
                  locale={locale}
                  renderInPortal={false}
                />
              </RuleValueWrapper>
            )}
          </FlexBox>

          <ValueWarningMessage message={colorWarningMessage} />
        </FlexBox>
      </InputsWrapper>
      {!isNewConditionalFormatting && showInvertCheckbox(comparator) && (
        <InvertCheckbox
          isInverted={isInverted}
          onChange={isInverted => updateRule(ruleId, { isInverted })}
        />
      )}

      {!isNewConditionalFormatting ? (
        <FormattingColorPicker
          dataTestId="formatting-rule-color-picker"
          value={color}
          onValueChange={color => updateRule(ruleId, { color })}
          isNewConditionalFormatting={isNewConditionalFormatting}
        />
      ) : null}
    </>
  );
};
